import { CommonModule } from '@angular/common';
import { Component, inject, NgZone, OnDestroy } from '@angular/core';
import { BasePaymentComponent, LANGUAGE } from '@fgb/core';
import { FormsModule } from '@angular/forms';
import { LoadingModule } from '@fgb/portal-component-library/src/lib/shared/loading';
import { FGBSharedModule } from '@fgb/portal-component-library/src/lib/shared';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

declare var Clover: any;

export type CloverFieldKey = 'CARD_NUMBER' | 'CARD_CVV' | 'CARD_DATE' | 'CARD_POSTAL_CODE';

@Component({
  selector: 'fgbcl-payment-clover',
  standalone: true,
  imports: [CommonModule, FormsModule, LoadingModule, FGBSharedModule],
  templateUrl: './payment-clover.component.html',
  styleUrl: './payment-clover.component.scss',
})
export class PaymentCloverComponent extends BasePaymentComponent implements OnDestroy {
  private clover: any;
  private zone = inject(NgZone);
  errors: Record<CloverFieldKey, string>;
  private router = inject(Router);
  private paymentRequestGoogleButton: any;
  private translateService = inject(TranslateService);
  private languages = inject(LANGUAGE);
  private modal = inject(NgbModal);
  isLoading: boolean = false;
  hasError: boolean = false;

  /* ================================
   * Lifecycle Hooks
   * ================================ */

  ngOnDestroy(): void {
    if (this.paymentRequestGoogleButton) {
      this.paymentRequestGoogleButton.removeEventListener('paymentMethod', this.paymentHandler.bind(this));
    }

    const iframe = document.getElementById('INTERMEDIATE_ID');
    if (iframe && iframe.parentNode) {
      iframe.parentNode.removeChild(iframe);
    }

    super.ngOnDestroy();
  }

  /* ================================
   * Public API
   * ================================ */

  init(): void {
    if (!(window as any).Clover) {
      console.error('Clover SDK not loaded yet');
      return;
    }

    const currentLang = this.getCurrentLanguage(this.translateService.currentLang);

    const apiAccessKey = this.properties.apiKey;
    const merchantId = this.properties.merchantId;
    const locale = 'en-US';

    this.zone.runOutsideAngular(() => {
      this.clover = new Clover(apiAccessKey, {
        merchantId: merchantId,
        locale: locale,
      });

      const styles = {
        body: {
          fontFamily: 'Roboto, Open Sans, sans-serif',
          fontSize: '16px',
        },
        input: {
          fontSize: '16px',
        },
      };

      const applePayRequest = this.clover.createApplePaymentRequest({
        amount: this.amount,
        countryCode: currentLang.country,
        currencyCode: currentLang.currency,
      });
      const elements = this.clover.elements();

      const cardNumber = elements.create('CARD_NUMBER', styles);
      const cardDate = elements.create('CARD_DATE', styles);
      const cardCvv = elements.create('CARD_CVV', styles);
      const cardPostalCode = elements.create('CARD_POSTAL_CODE', styles);
      const paymentRequestAppleButton = elements.create('PAYMENT_REQUEST_BUTTON_APPLE_PAY', {
        applePaymentRequest: applePayRequest,
        sessionIdentifier: this.orderId,
      });

      const paymentReqData = {
        country: currentLang.country,
        currency: currentLang.currency,
        total: {
          label: 'Demo total',
          amount: this.amount,
        },
        // Default buttonType is 'long' for button with card brand & last 4 digits
        options: {
          button: {
            buttonType: 'short', // For button without additional text
          },
        },
      };

      this.paymentRequestGoogleButton = elements.create('PAYMENT_REQUEST_BUTTON', {
        paymentReqData,
      });
      this.paymentRequestGoogleButton.addEventListener('paymentMethod', this.paymentHandler.bind(this));

      cardNumber.mount('#card-number');
      cardDate.mount('#card-date');
      cardCvv.mount('#card-cvv');
      cardPostalCode.mount('#card-postal-code');
      paymentRequestAppleButton.mount('#card-apple');
      this.paymentRequestGoogleButton.mount('#card-google');
    });
  }

  submitForm(event: any): void {
    event.preventDefault();
    this.submit();
  }

  submit(): void {
    if (this.isLoading) return;
    this.zone.runOutsideAngular(() => {
      this.hasError = false;
      this.clover.createToken().then((result: any) => {
        if (result) {
          if (result.errors) {
            this.errors = result.errors;
            this.hasError = true;
          } else {
            this.requestPayment(result.token);
          }
        }
        this.isLoading = false;
      });
    });
  }

  /* ================================
   * Private Helpers
   * ================================ */

  private paymentHandler(e: any): void {
    if (e.errors) {
      this.hasError = true;
      return;
    }
    this.hasError = false;
    this.requestPayment(e.token);
  }

  private requestPayment(token: string): void {
    if (this.isLoading || !token) return;
    this.zone.run(() => {
      const callbackUrl = this.context.callbackUrl;
      const params = { orderId: this.orderId, sourcekey: token };

      this.router.navigate([callbackUrl], { queryParams: params });
      this.modal.dismissAll();
      this.ngOnDestroy(); // manual destroy since it's not triggering after modal dismissAll
    });
  }

  // Get current language from the supported languages and return currency and country code
  private getCurrentLanguage(lang: string) {
    const { currencyCode, flag } = this.languages.find((l) => l.lang === lang) || this.languages[0];
    return { currency: currencyCode, country: flag };
  }
}
